"use client"

import { useEffect, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { isVersionSkewError, VERSION_SKEW_EVENT } from "@/lib/deploy/versionSkew"
import { watchesDeployments } from "@/lib/deploy/updateSurface"
import {
  decideIdleReload,
  isBusy,
  jitterFrom,
  LAST_AUTO_RELOAD_KEY,
  readLastAutoReload,
  RECHECK_MS,
  type IdleReloadDecision,
} from "@/lib/deploy/idleReload"
import { CLIENT_EPOCH } from "@/lib/deploy/clientEpoch"
import { isInService, locationSlugFromPathname, type LocationSchedule } from "@/lib/deploy/serviceWindow"
import { nativeSafeReload } from "@/lib/capacitor/nav"

// SESS-15: kiosk tablets are always-visible SPAs that run for days — they never
// fire focus/visibilitychange, so without a timer they NEVER learn a new build
// shipped and keep running a stale bundle across a whole day of deploys (the
// 2026-07-17 wave: 16 deploys, 249 skew 404s, operators wiping healthy
// sessions). The check is a tiny GET /api/build-id; 5 min keeps the stale
// window shorter than a dinner rush without meaningful load.
const BUILD_CHECK_INTERVAL_MS = 5 * 60 * 1000
/** El horario del local se refresca como mucho cada hora mientras haya algo pendiente. */
const SCHEDULE_TTL_MS = 60 * 60 * 1000

function deviceTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function BuildWatcher({ initialId }: { initialId: string }) {
  const knownId = useRef(initialId)
  // outdated: hay un deployment nuevo. required: ese deployment declaró que
  // este bundle ya no sirve (CLIENT_EPOCH). skew: una action/chunk ya falló por
  // desfase de versión. Son señales distintas porque la política las trata
  // distinto durante el servicio (S5-1, ver lib/deploy/idleReload.ts).
  const [outdated, setOutdated] = useState(false)
  const [required, setRequired] = useState(false)
  const [skew, setSkew] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [decision, setDecision] = useState<IdleReloadDecision | null>(null)
  const t = useTranslations("common.update")
  const pathname = usePathname()
  // Solo las superficies de operador vigilan el deployment vivo. En las páginas
  // públicas (marketing, microsite, /order, PWA de mesa) el aviso interrumpía al
  // cliente sin darle nada: su visita dura minutos y la próxima carga ya trae la
  // build nueva. Ver lib/deploy/updateSurface.ts.
  const pollsDeployments = watchesDeployments(pathname)
  const slug = locationSlugFromPathname(pathname)

  // ── Sondeo del deployment vivo ───────────────────────────────────────────
  // Sigue sondeando aunque ya haya versión nueva: un deploy NECESARIO puede
  // llegar después de uno normal, y solo ese recarga durante el servicio.
  useEffect(() => {
    if (!pollsDeployments || required) return

    const check = async () => {
      try {
        const res = await fetch(`/api/build-id?epoch=${CLIENT_EPOCH}`, { cache: "no-store" })
        if (!res.ok) return
        const body = (await res.json()) as { id?: string; required?: boolean }
        // "dev" means local dev — never prompt
        if (body.id && body.id !== "dev" && body.id !== knownId.current) setOutdated(true)
        if (body.required === true) {
          setOutdated(true)
          setRequired(true)
        }
      } catch {
        // network error — ignore silently
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === "visible") check()
    }

    const intervalId = setInterval(check, BUILD_CHECK_INTERVAL_MS)
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("focus", check)
    return () => {
      clearInterval(intervalId)
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("focus", check)
    }
  }, [pollsDeployments, required])

  // ── Desfase de versión (skew) ────────────────────────────────────────────
  // A stale JS chunk or Server Action means the running code no longer matches
  // the live deployment: la tablet ya está rota y se recarga en el siguiente
  // momento sin uso AUNQUE el local esté en servicio (un cobro o una acción de
  // cocina en curso la retienen vía holdReload()). Vercel Skew Protection
  // prevents most of these; this is the fallback for the out-of-window case.
  // Los listeners se arman en TODAS las superficies: ahí la página ya está rota
  // y el aviso es la salida, no un nag. See lib/deploy/versionSkew.ts.
  useEffect(() => {
    if (skew) return
    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      if (isVersionSkewError(e.reason)) setSkew(true)
    }
    const onError = (e: ErrorEvent) => {
      if (isVersionSkewError(e.error ?? e.message)) setSkew(true)
    }
    // SESS-15: skew errors caught by a component's own try/catch (PinGate,
    // PosReadTokenProvider) and 404s of Server Action POSTs (ActionSkewSensor)
    // are re-reported via this event — they never reach the handlers above.
    const onReportedSkew = () => setSkew(true)

    window.addEventListener("unhandledrejection", onUnhandledRejection)
    window.addEventListener("error", onError)
    window.addEventListener(VERSION_SKEW_EVENT, onReportedSkew)
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
      window.removeEventListener("error", onError)
      window.removeEventListener(VERSION_SKEW_EVENT, onReportedSkew)
    }
  }, [skew])

  // ── Auto-recarga controlada (egress 2026-08-15 → S5-1 2026-10-10) ────────
  // Un fix de cliente que nadie recarga no existe (#1528), pero recargar en
  // cada pausa de 90 s con 40-50 deploys al día eran ~40 arranques en frío por
  // tablet en plena cena (P1-15). La política (lib/deploy/idleReload.ts):
  // durante el servicio del local solo recarga lo urgente (required o skew);
  // un deploy normal deja un indicador discreto y espera al cierre. Siempre
  // exige: sin holdReload (cobro en vuelo, print en curso), con red, IDLE_MS
  // sin toques, jitter cumplido y sin otra recarga automática reciente. El
  // carrito del POS y el estado "esperando terminal" sobreviven un reload
  // (useCart persistido, C-05).
  // Se siembra al montar el efecto de abajo (Date.now() en render es impuro).
  const lastInteractionRef = useRef<number>(0)
  const pendingAtRef = useRef<number | null>(null)
  const jitterRef = useRef<number>(0)
  const scheduleRef = useRef<LocationSchedule | null>(null)
  const scheduleAttemptedRef = useRef(false)
  const scheduleFetchedAtRef = useRef(0)
  const scheduleInFlightRef = useRef(false)

  useEffect(() => {
    if (!pollsDeployments) return
    const touch = () => { lastInteractionRef.current = Date.now() }
    touch() // montar cuenta como interacción: nunca recargar en el primer instante
    const opts: AddEventListenerOptions = { passive: true, capture: true }
    window.addEventListener("pointerdown", touch, opts)
    window.addEventListener("keydown", touch, opts)
    window.addEventListener("touchstart", touch, opts)
    return () => {
      window.removeEventListener("pointerdown", touch, opts)
      window.removeEventListener("keydown", touch, opts)
      window.removeEventListener("touchstart", touch, opts)
    }
  }, [pollsDeployments])

  useEffect(() => {
    if ((!outdated && !skew) || !pollsDeployments || reloading) return
    if (pendingAtRef.current === null) {
      pendingAtRef.current = Date.now()
      jitterRef.current = jitterFrom(Math.random())
    }

    // Horario del local: solo se pide cuando YA hay algo pendiente (pocas veces
    // al día) y el CDN lo cachea por slug. Sin slug o si falla, null → ventana
    // nocturna (lib/deploy/serviceWindow.ts).
    const refreshSchedule = () => {
      if (scheduleInFlightRef.current) return
      if (scheduleAttemptedRef.current && Date.now() - scheduleFetchedAtRef.current < SCHEDULE_TTL_MS) return
      if (!slug) {
        scheduleAttemptedRef.current = true
        scheduleFetchedAtRef.current = Date.now()
        return
      }
      scheduleInFlightRef.current = true
      fetch(`/api/build-id/schedule?slug=${encodeURIComponent(slug)}`)
        .then((res) => (res.ok ? (res.json() as Promise<LocationSchedule>) : null))
        .then((body) => { if (body) scheduleRef.current = body })
        .catch(() => {})
        .finally(() => {
          scheduleInFlightRef.current = false
          scheduleAttemptedRef.current = true
          scheduleFetchedAtRef.current = Date.now()
        })
    }

    const tick = () => {
      refreshSchedule()
      const now = Date.now()
      // Mientras no se intentó leer el horario, se asume EN SERVICIO: mejor
      // esperar un tick que recargar en plena cena por un fallback nocturno.
      const inService = scheduleAttemptedRef.current
        ? isInService({ schedule: scheduleRef.current, nowMs: now, deviceTimezone: deviceTimezone() })
        : true
      const next = decideIdleReload({
        operatorSurface: pollsDeployments,
        outdated,
        required,
        skew,
        inService,
        online: typeof navigator === "undefined" || navigator.onLine !== false,
        busy: isBusy(),
        msSinceInteraction: now - lastInteractionRef.current,
        msSinceOutdated: now - (pendingAtRef.current ?? now),
        jitterMs: jitterRef.current,
        msSinceLastAutoReload: readLastAutoReload(safeLocalStorage(), now),
      })
      setDecision(next)
      if (next === "reload") {
        try {
          safeLocalStorage()?.setItem(LAST_AUTO_RELOAD_KEY, String(now))
        } catch {
          // sin storage no hay anti-bucle persistente; el resto de condiciones sigue
        }
        setReloading(true)
        nativeSafeReload()
      }
    }
    tick()
    const id = setInterval(tick, RECHECK_MS)
    return () => clearInterval(id)
  }, [outdated, required, skew, pollsDeployments, reloading, slug])

  if (!outdated && !skew) return null

  const handleUpdate = () => {
    setReloading(true)
    // Same-origin hard reload. Android stays in-app via MainActivity's host
    // check; iOS necesita el desvío por /tablet de nativeSafeReload — un reload
    // plano fuera de /tablet rebota a Safari (ver lib/capacitor/nav.ts).
    nativeSafeReload()
  }

  // Versión nueva NO urgente durante el servicio: un indicador discreto en una
  // esquina, nunca el aviso centrado que tapa la caja. Tocarlo actualiza ya.
  // Antes del primer tick también, para que el aviso grande no parpadee.
  const discreet = decision === "defer" || (decision === null && !skew && !required)
  if (discreet) {
    return (
      <button
        type="button"
        onClick={handleUpdate}
        disabled={reloading}
        title={t("pillHint")}
        aria-label={`${t("pill")}. ${t("pillHint")}`}
        style={{
          position: "fixed",
          left: "8px",
          bottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
          zIndex: 9999,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px 4px 8px",
          borderRadius: "9999px",
          border: "1px solid var(--redy-border)",
          background: "var(--redy-surface)",
          color: "var(--redy-secondary)",
          fontSize: "11px",
          fontWeight: 600,
          lineHeight: 1.4,
          opacity: reloading ? 0.6 : 0.92,
          cursor: reloading ? "default" : "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        <span
          aria-hidden
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "9999px",
            flexShrink: 0,
            background: "var(--redy-gradient)",
          }}
        />
        {reloading ? t("updating") : t("pill")}
      </button>
    )
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: "16px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "14px",
        maxWidth: "calc(100vw - 24px)",
        padding: "12px 14px 12px 18px",
        borderRadius: "14px",
        background: "var(--redy-charcoal, #1A1A1A)",
        color: "#ffffff",
        boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
        fontSize: "14px",
        fontWeight: 500,
      }}
    >
      <span
        aria-hidden
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "9999px",
          flexShrink: 0,
          background:
            "var(--redy-gradient, linear-gradient(135deg, #D7263D, #F4722B))",
        }}
      />
      <span style={{ lineHeight: 1.3 }}>{t("available")}</span>
      <button
        type="button"
        onClick={handleUpdate}
        disabled={reloading}
        style={{
          flexShrink: 0,
          padding: "8px 16px",
          borderRadius: "10px",
          border: "none",
          cursor: reloading ? "default" : "pointer",
          color: "#ffffff",
          fontSize: "14px",
          fontWeight: 700,
          opacity: reloading ? 0.7 : 1,
          background:
            "var(--redy-gradient, linear-gradient(135deg, #D7263D, #F4722B))",
        }}
      >
        {reloading ? t("updating") : t("cta")}
      </button>
    </div>
  )
}
